import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Activity, Utensils, Bell, Brain, BarChart3, Shield, 
  Star, Check, ArrowRight, Play, Users, Heart, Zap,
  Smartphone, Globe, Award, ChevronRight
} from 'lucide-react';

const LandingPage = () => {
  const [email, setEmail] = useState('');

  const features = [
    { icon: Activity, title: 'Blood Sugar Tracking', description: 'Log and monitor your glucose levels with smart insights', color: 'text-red-500' },
    { icon: Utensils, title: '1000+ Recipes', description: 'Diabetes-friendly recipes with full nutrition info', color: 'text-orange-500' },
    { icon: Brain, title: 'AI-Powered Insights', description: 'Get personalized recommendations from our AI', color: 'text-purple-500' },
    { icon: Smartphone, title: 'Food Scanner', description: 'Scan barcodes to instantly get nutrition data', color: 'text-blue-500' },
    { icon: Bell, title: 'Smart Reminders', description: 'Never miss a medication or glucose check', color: 'text-green-500' },
    { icon: BarChart3, title: 'Health Analytics', description: 'Track trends and share reports with your doctor', color: 'text-teal-500' },
  ];

  const testimonials = [
    { name: 'Sarah M.', role: 'Type 2 Diabetic', text: 'DiaWise helped me lower my A1C by 1.5 points in 3 months!', rating: 5, avatar: '👩‍🦰' },
    { name: 'James K.', role: 'Type 1 Diabetic', text: 'The food scanner is a game-changer. I use it every day.', rating: 5, avatar: '👨' },
    { name: 'Maria L.', role: 'Pre-diabetic', text: 'The recipes are delicious and my blood sugar is stable now.', rating: 5, avatar: '👩' },
  ];

  const stats = [
    { value: '50,000+', label: 'Active Users' },
    { value: '1M+', label: 'Glucose Logs' },
    { value: '4.8', label: 'App Rating' },
    { value: '98%', label: 'Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">DiaWise</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900">Features</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900">Testimonials</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Pricing</a>
              <Link to="/login">
                <Button variant="outline">Log In</Button>
              </Link>
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700">Start Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 px-4 py-2">
            🎉 Join 50,000+ users managing diabetes smarter
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Your Personal
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent"> Diabetes </span>
            Assistant
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Track blood sugar, discover 1000+ recipes, get AI-powered insights, and take control of your health journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 h-14">
                Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="text-lg px-8 h-14">
              <Play className="mr-2 w-5 h-5" /> Watch Demo
            </Button>
          </div>
          <p className="mt-4 text-sm text-gray-500">No credit card required • 7-day Pro trial included</p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Features</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Comprehensive tools to manage your diabetes effectively</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4`}>
                    <feature.icon className={`w-7 h-7 ${feature.color}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section id="testimonials" className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-purple-100 text-purple-700">Testimonials</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Loved by Thousands</h2>
            <p className="text-xl text-gray-600">See what our users are saying</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4">"{testimonial.text}"</p>
                  <div className="flex items-center">
                    <span className="text-3xl mr-3">{testimonial.avatar}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section id="pricing" className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-green-100 text-green-700">Pricing</Badge>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-xl text-gray-600 mb-8">Start free, upgrade when you're ready</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-gray-300 transition-colors">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Free</h3>
                <p className="text-4xl font-bold mb-4">$0<span className="text-lg text-gray-500">/mo</span></p>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Basic tracking</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> 10 recipes</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Food scanner</li>
                </ul>
                <Link to="/login"><Button variant="outline" className="w-full">Get Started</Button></Link>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-emerald-500 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-emerald-500 text-white">Most Popular</Badge>
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Pro</h3>
                <p className="text-4xl font-bold mb-4">$9.99<span className="text-lg text-gray-500">/mo</span></p>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Unlimited tracking</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> 1000+ recipes</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> AI insights</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Data export</li>
                </ul>
                <Link to="/login"><Button className="w-full bg-emerald-600 hover:bg-emerald-700">Start Free Trial</Button></Link>
              </CardContent>
            </Card>
            
            <Card className="border-2 hover:border-gray-300 transition-colors">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-2">Enterprise</h3>
                <p className="text-4xl font-bold mb-4">$29.99<span className="text-lg text-gray-500">/mo</span></p>
                <ul className="text-left space-y-2 mb-6">
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Everything in Pro</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Family accounts</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Care team access</li>
                  <li className="flex items-center"><Check className="w-4 h-4 mr-2 text-green-500" /> Priority support</li>
                </ul>
                <Link to="/login"><Button variant="outline" className="w-full">Contact Sales</Button></Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Get Diabetes Tips & Recipes Weekly</h2>
          <p className="text-emerald-100 mb-8">Join 25,000+ subscribers getting our free newsletter</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-white text-gray-900 h-12 w-full sm:w-80"
            />
            <Button className="bg-white text-emerald-600 hover:bg-emerald-50 h-12 px-8">
              Subscribe Free
            </Button>
          </div>
          <p className="text-sm text-emerald-200 mt-3">No spam. Unsubscribe anytime.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                  <Activity className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">DiaWise</span>
              </div>
              <p className="text-gray-400">Your trusted partner in diabetes management.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><Link to="/recipes" className="hover:text-white">Recipes</Link></li>
                <li><Link to="/shop" className="hover:text-white">Shop</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">About Us</a></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-white">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 DiaWise. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
